import { message } from "antd";
import { useState } from "react";
import employeeService from "Services/AdminServices/Organization/EmployeeService";
import { useNavigate } from "react-router-dom";

// Per Mudassir (May-20): caller can opt into the employee-authorized
// `/me/career-path/alternatives` endpoint by passing { useMeEndpoint: true }.
// HR-side pages keep the default behaviour (uses employeeId path param).
export const useAlternativeCareerPaths = (employeeId, options = {}) => {
  const { useMeEndpoint = false } = options;
  const navigate =  useNavigate();
  const [careerPaths, setCareerPaths] = useState(null);
  const [loadingCareerPaths, setLoadingCareerPaths] = useState(false);
  const [careerPathsLoaded, setCareerPathsLoaded] = useState(false);
  // Executive-employee scenario: API returns { isApplicable: false, message }.
  const [isApplicable, setIsApplicable] = useState(true);
  const [notApplicableMessage, setNotApplicableMessage] = useState("");
  // Per Mudassir (May-22): new explicit flags replace heuristic detection.
  // hasAlternativeCareerPaths — are there paths in DB at all
  // isAiRecommendationAvailable — did AI insights generate successfully
  // backendMessage — UI-friendly text from backend for the current state
  const [hasAlternativeCareerPaths, setHasAlternativeCareerPaths] = useState(true);
  const [isAiRecommendationAvailable, setIsAiRecommendationAvailable] = useState(true);
  const [backendMessage, setBackendMessage] = useState("");
  const [performanceReview, setPerformanceReview] = useState([]);
  const [loadingPerformanceReview, setLoadingPerformanceReview] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchAlternativeCareerPaths = async (employeeId) => {
    try {
      setLoadingCareerPaths(true);

      const response = useMeEndpoint
        ? await employeeService.getMyAlternativePaths()
        : await employeeService.getAlternativePaths(employeeId);
      // Service returns { isApplicable, hasAlternativeCareerPaths,
      // isAiRecommendationAvailable, message, data }. Components rely on
      // careerPaths being the unwrapped data (so existing renders keep working).
      setIsApplicable(response.isApplicable);
      setHasAlternativeCareerPaths(response.hasAlternativeCareerPaths);
      setIsAiRecommendationAvailable(response.isAiRecommendationAvailable);
      setBackendMessage(response.message || "");
      // Keep notApplicableMessage for backwards-compat with existing
      // executive-empty-state callers.
      setNotApplicableMessage(response.message || "");
      setCareerPaths(response.data || null);
      setCareerPathsLoaded(true);
    } catch (error) {
      console.error("Error in fetching career paths", error);
    } finally {
      setLoadingCareerPaths(false);
    }
  };

  const fetchPerformanceReview = async (employeeId) => {
    try {
      setLoadingPerformanceReview(true);
      const data = await employeeService.getPreviousReview(employeeId);
      setPerformanceReview(data);
    } catch (error) {
      console.error("Error in fetching Performance Review", error);
    } finally {
      setLoadingPerformanceReview(false);
    }
  };

  const handleOpenModal = (path, employeeId) => {
    setSelectedPath(path);
    setSelectedSupervisor(null);
    setSelectedJob(null);
    setModalVisible(true);
    fetchSupervisors(employeeId, path.id);
  };

  const handleJobChange = (jobId) => {
  setSelectedJob(jobId);
  setSelectedSupervisor(null);
  setSupervisors([]);

  fetchSupervisors(employeeId, jobId); //  updated call
};

  const fetchSupervisors = async (employeeId, jobTitleId) => {
    try {
      setLoadingSupervisors(true);
      const data = await employeeService.getSupervisors(employeeId, jobTitleId);
      setSupervisors(data);
    } catch (error) {
      console.error("Error in fetching Supervisors", error);
    } finally {
      setLoadingSupervisors(false);
    }
  };

  const handleSubmitTransition = async () => {
    if (!selectedSupervisor || !selectedJob) {
      message.warning("Please select supervisor and job role");
      return;
    }

    const payload = {
      employeeId: Number(employeeId),
      careerPathId: selectedPath.id,
      jobTitleId: selectedJob,
      supervisorId: selectedSupervisor,
    };

    try {
      await employeeService.createTransition(payload);

      message.success("Transition created successfully!");
      setModalVisible(false);
  
    navigate(`/employeeDetail/${employeeId}`, {
  state: {
    activeTab: "3",
    refresh: true,
  },
});
    } catch (error) {
      console.error("Failed to create transition");
    }
  };

  return {
    careerPaths,
    loadingCareerPaths,
    careerPathsLoaded,
    fetchAlternativeCareerPaths,

    // Executive-employee scenario flags (per Mudassir May-20 doc).
    isApplicable,
    notApplicableMessage,

    // Explicit alt-paths/AI flags (per Mudassir May-22).
    hasAlternativeCareerPaths,
    isAiRecommendationAvailable,
    backendMessage,

    loadingPerformanceReview,
    performanceReview,
    fetchPerformanceReview,

    // supervisors
    fetchSupervisors,
    loadingSupervisors,
    supervisors,

    // modal state
    selectedPath,
    setSelectedPath,
    selectedSupervisor,
    setSelectedSupervisor,
    selectedJob,
    setSelectedJob,
    modalVisible,
    setModalVisible,

    // actions
    handleOpenModal,
    handleSubmitTransition,
    handleJobChange
  };
};
